import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Move a media slide to the front of the gallery (one-column grid hero).
   * @param {string} mediaId
   */
  #promoteMediaToFront(mediaId) {
    if (!mediaId) return false;
    const match = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!match) return false;
    const slide = match.closest('slideshow-slide') || match.closest('.product-media-container') || match;
    const list = slide.parentElement;
    if (!list) return false;
    if (list.firstElementChild !== slide) {
      list.insertBefore(slide, list.firstElementChild);
    }
    this.slideshow?.select?.(0, undefined, { animate: false });
    return true;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Featured media id from server-ordered gallery (first [data-media-id])
    const featuredEl = newMediaGallery.querySelector('[data-media-id]');
    const featuredId = featuredEl?.getAttribute('data-media-id') || '';

    // If snappy skipped replaceWith (same media set), still promote hero.
    // Try promote on the live gallery first when the featured frame already exists.
    if (featuredId && this.querySelector(`[data-media-id="${featuredId}"]`)) {
      this.#promoteMediaToFront(featuredId);
    }

    this.replaceWith(newMediaGallery);

    // After remount, ensure featured is first (belt-and-suspenders).
    queueMicrotask(() => {
      const live = document.querySelector('media-gallery');
      if (!live || !featuredId) return;
      const match = live.querySelector(`[data-media-id="${featuredId}"]`);
      const slide = match?.closest('slideshow-slide') || match?.closest('.product-media-container');
      const list = slide?.parentElement;
      if (slide && list && list.firstElementChild !== slide) {
        list.insertBefore(slide, list.firstElementChild);
      }
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
